import React, { useRef, useState } from "react";
import { Box, Stack, Button, Grid, IconButton, Typography, Card, CardMedia } from "@mui/material";
import { BiLoader, BiTrash, BiImageAdd } from "react-icons/bi";

/**
 * Generated by: ChatGPT-5
 * 
 * ImageGalleryUploader (Plain JS + MUI v7 + react-icons/bi)
 *
 * Props
 * - images: string[]                 // current array of image URLs (state from parent)
 * - setImages: (urls: string[] | ((prev: string[]) => string[])) => void
 * - onUpload?: (file: File) => Promise<string>  // optional uploader that returns a public URL
 * - maxImages?: number               // optional cap
 *
 * Notes
 * - Uses FileReader to create a data URL preview immediately.
 * - If onUpload is provided, it will replace the preview URL with the final public URL when resolved.
 */
const ImageGalleryUploader = ({ images, setImages, onUpload, maxImages }) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  // Default mock uploader — replace by passing `onUpload` prop
  const mockUpload = async (file) => {
    // Fake latency
    await new Promise((r) => setTimeout(r, 700));
    // Pretend the preview URL is already public (keep as-is)
    return null; // returning null indicates "keep the preview URL"
  };

  const handleBrowseClick = () => fileInputRef.current?.click();

  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFilesSelected = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setError(null);
    setIsUploading(true);

    const uploader = onUpload || mockUpload;

    try {
      // Process sequentially to maintain predictable index placement
      for (const file of Array.from(files)) {
        if (!file.type?.startsWith("image/")) {
          throw new Error(`Unsupported file type: ${file.type || file.name}`);
        }
        if (file.size > 20 * 1024 * 1024) {
          throw new Error(`${file.name} is larger than 20MB.`);
        }

        // 1) Immediate preview with FileReader
        const previewUrl = await readFileAsDataURL(file);
        let insertedIndex;
        setImages((images) => {
          const next = [...images, previewUrl];
          insertedIndex = next.length - 1;
          return typeof maxImages === "number" ? next.slice(0, maxImages) : next;
        });

        // 2) Upload and, if the uploader returns a public URL, replace the preview
        try {
          const publicUrl = await uploader(file);
          if (publicUrl) {
            setImages((images) => images.map((u, i) => (i === insertedIndex ? publicUrl : u)));
          }
        } catch (uploadErr) {
          // If upload fails, remove the preview we just added
          setImages((images) => images.filter((_, i) => i !== insertedIndex));
          throw uploadErr;
        }
      }
    } catch (err) {
      setError(err?.message || "Failed to upload image(s).");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = ""; // allow re-selecting same file
    }
  };

  const handleRemove = (index) => {
    setImages((images) => images.filter((_, i) => i !== index));
  };

  const remaining = typeof maxImages === "number" ? Math.max(maxImages - images.length, 0) : undefined;

  return (
    <Box sx={{ width: "100%" }}>
                {/* Controls */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Button
          variant="outlined"
          onClick={handleBrowseClick}
          disabled={isUploading || remaining === 0}
          startIcon={isUploading ? <BiLoader className="spin" /> : <BiImageAdd />}
        >
          {isUploading ? "Uploading…" : "Add image"}
        </Button>
        {typeof remaining === "number" && (
          <Typography variant="body2" color="text.secondary">{remaining} remaining</Typography>
        )}
      </Stack>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFilesSelected}
        style={{ display: "none" }}
      />

      {/* Error */}
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }} role="alert">
          {error}
        </Typography>
      )}

      {/* Empty state */}
      {images.length === 0 && (
        <Box
          sx={{
            mt: 2,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 3,
            p: 4,
            textAlign: "center",
          }}
        >
          <BiImageAdd size={40} style={{ marginBottom: 8 }} />
          <Typography variant="body2" color="text.secondary">
            No images yet. Click “Add image” to upload.
          </Typography>
        </Box>
      )}

      {/* Gallery */}
      {images.length > 0 && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {images.map((url, idx) => (
            <Grid key={`${url}-${idx}`} size={{xs:12, sm:6, md:6, lg:4}}>
              <Card sx={{ position: "relative", borderRadius: 3, overflow: "hidden" }}>
                <CardMedia component="img" image={url} alt={`Company image ${idx + 1}`} sx={{ width: '600px', objectFit: 'contain' }} />
                <IconButton
                  aria-label={`Remove image ${idx + 1}`}
                  title="Remove"
                  onClick={() => handleRemove(idx)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    bgcolor: "background.paper",
                    '&:hover': { bgcolor: "background.paper" },
                    boxShadow: 1,
                  }}
                >
                  <BiTrash />
                </IconButton>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tiny CSS for spinner on the upload icon */}
      <style jsx>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Box>
  );
}

export default ImageGalleryUploader;
